/**
 * Die Startseite der oeffentlichen Testfassung.
 *
 * Links steht, worum es geht, rechts das Formular. Wer neu ist, registriert
 * sich mit Vorname, Nachname, E-Mail und Passwort. Wer schon war, meldet sich
 * an. Das Haekchen schliesst Nutzungsbedingungen und Auftragsverarbeitung ab,
 * ohne das gibt es keinen Zugang.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router'
import { ApiFehler } from '../lib/api'
import { useAnmeldung } from '../lib/anmeldung'
import type { Anmeldedaten, Teststand } from '../lib/test'

const ENTWURF = 'camt.registrierung'
type Entwurf = Partial<Record<'vorname' | 'nachname' | 'kanzlei' | 'email', string>>

/** Was jemand schon eingetippt hatte, bevor er die Bedingungen gelesen hat. Ohne Speicher: leer. */
function entwurfLesen(): Entwurf {
  try {
    const roh = localStorage.getItem(ENTWURF)
    return roh ? (JSON.parse(roh) as Entwurf) : {}
  } catch {
    return {}
  }
}

export function TestStartSeite({
  stand,
  registrieren,
}: {
  stand: Teststand
  registrieren: (daten: Anmeldedaten) => Promise<void>
}) {
  const { anmelden } = useAnmeldung()
  // Der Entwurf kommt direkt in den Anfangszustand, nicht per Effekt: sonst speichert der
  // Speicher-Effekt die leeren Felder, bevor der Lese-Effekt die alten Werte zurueckholt.
  const entwurf = entwurfLesen()
  const [modus, setzeModus] = useState<'neu' | 'zurueck'>('neu')
  const [vorname, setzeVorname] = useState(entwurf.vorname ?? '')
  const [nachname, setzeNachname] = useState(entwurf.nachname ?? '')
  const [kanzlei, setzeKanzlei] = useState(entwurf.kanzlei ?? '')
  const [email, setzeEmail] = useState(entwurf.email ?? '')
  const [passwort, setzePasswort] = useState('')
  const [wiederholung, setzeWiederholung] = useState('')
  const [passwortSichtbar, setzePasswortSichtbar] = useState(false)
  const [zugestimmt, setzeZugestimmt] = useState(false)
  const [fehler, setzeFehler] = useState<string | null>(null)
  const [laeuft, setzeLaeuft] = useState(false)

  // Wer zwischendurch die Bedingungen liest, soll nicht alles neu tippen: Name, Kanzlei und
  // E-Mail bleiben im Browser, bis der Zugang angelegt ist. Das Passwort bewusst nicht.
  // localStorage statt sessionStorage, weil die Bedingungen in einem neuen Tab aufgehen und
  // "Zurueck zum Test" dort landet: der neue Tab muss die Eingaben auch kennen (Sven, 20.09.2026).
  useEffect(() => {
    try {
      localStorage.setItem(ENTWURF, JSON.stringify({ vorname, nachname, kanzlei, email }))
    } catch {
      // siehe oben
    }
  }, [vorname, nachname, kanzlei, email])

  async function absenden(ereignis: FormEvent) {
    ereignis.preventDefault()
    if (modus === 'neu' && passwort.length < 12) {
      setzeFehler('Das Passwort braucht mindestens zwölf Zeichen.')
      return
    }
    if (modus === 'neu' && passwort !== wiederholung) {
      setzeFehler('Die beiden Passwörter sind nicht gleich.')
      return
    }
    if (modus === 'neu' && !zugestimmt) {
      setzeFehler('Bitte bestätigen Sie die Bedingungen, dann geht es los.')
      return
    }
    setzeFehler(null)
    setzeLaeuft(true)
    try {
      if (modus === 'neu') {
        await registrieren({ vorname, nachname, email, passwort, ...(kanzlei ? { kanzlei } : {}) })
        try {
          localStorage.removeItem(ENTWURF)
        } catch {
          // egal
        }
        // Der Server hat die Sitzung schon gesetzt; die Oberflaeche holt sie ab.
        window.location.assign('/eingang')
      } else {
        await anmelden(email, passwort)
      }
    } catch (problem) {
      setzeFehler(problem instanceof ApiFehler ? problem.message : 'Das hat gerade nicht geklappt.')
    } finally {
      setzeLaeuft(false)
    }
  }

  return (
    <div className="test-seite">
      <div className="test-gitter">
        <div className="test-text">
          <div className="anmeldung-marke">
            <div className="wortmarke">
              H&amp;K <span>Automation</span>
            </div>
            <div className="werkzeug">camt · Aus PDF wird DATEV</div>
          </div>

          <h1>Kontoauszüge als DATEV-Datei, ohne Abtippen</h1>
          <p className="vorspann">
            PDF hochladen, die KI liest ab, der Code rechnet nach. Heraus kommt eine camt.053-Datei für DATEV. Geht der
            Saldo nicht auf den Cent auf, entsteht gar keine Datei.
          </p>

          <div className="test-kasten">
            <h2>Der Testzugang</h2>
            <ul>
              <li>
                <b>{stand.auszuegeGrenze} Kontoauszüge</b>, kostenlos
              </li>
              <li>
                <b>Bis {stand.seitenJeDatei} Seiten</b> je PDF
              </li>
              <li>
                <b>Eigener Bereich</b>, nur für Sie sichtbar
              </li>
            </ul>
          </div>
        </div>

        <form className="test-formular" onSubmit={(e) => void absenden(e)}>
          <div className="test-reiter">
            <button
              type="button"
              className={modus === 'neu' ? 'an' : ''}
              onClick={() => {
                setzeModus('neu')
                setzeFehler(null)
              }}
            >
              Neu hier
            </button>
            <button
              type="button"
              className={modus === 'zurueck' ? 'an' : ''}
              onClick={() => {
                setzeModus('zurueck')
                setzeFehler(null)
              }}
            >
              Schon dabei
            </button>
          </div>

          {fehler && <div className="fehler">{fehler}</div>}

          {modus === 'neu' && (
            <>
              <div className="zwei-spalten">
                <div>
                  <label htmlFor="vorname">Vorname</label>
                  <input id="vorname" type="text" required autoComplete="given-name" value={vorname} onChange={(e) => setzeVorname(e.target.value)} />
                </div>
                <div>
                  <label htmlFor="nachname">Nachname</label>
                  <input id="nachname" type="text" required autoComplete="family-name" value={nachname} onChange={(e) => setzeNachname(e.target.value)} />
                </div>
              </div>

              <div>
                <label htmlFor="kanzlei">Kanzlei</label>
                <input
                  id="kanzlei"
                  type="text"
                  autoComplete="organization"
                  placeholder="Optional"
                  value={kanzlei}
                  onChange={(e) => setzeKanzlei(e.target.value)}
                />
              </div>
            </>
          )}

          <div>
            <label htmlFor="email">E-Mail</label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setzeEmail(e.target.value)}
            />
          </div>

          <div>
            <div className="beschriftung-zeile">
              <label htmlFor="passwort">Passwort</label>
              <button type="button" className="verweis-knopf klein" onClick={() => setzePasswortSichtbar((s) => !s)}>
                {passwortSichtbar ? 'Verbergen' : 'Anzeigen'}
              </button>
            </div>
            <input
              id="passwort"
              type={passwortSichtbar ? 'text' : 'password'}
              required
              autoComplete={modus === 'neu' ? 'new-password' : 'current-password'}
              value={passwort}
              onChange={(e) => setzePasswort(e.target.value)}
            />
            {modus === 'neu' && <p className="leiser klein">Mindestens zwölf Zeichen.</p>}
          </div>

          {modus === 'neu' && (
            <div>
              <label htmlFor="wiederholung">Passwort wiederholen</label>
              <input
                id="wiederholung"
                type={passwortSichtbar ? 'text' : 'password'}
                required
                autoComplete="new-password"
                value={wiederholung}
                onChange={(e) => setzeWiederholung(e.target.value)}
                aria-invalid={wiederholung.length > 0 && wiederholung !== passwort ? true : undefined}
              />
              {wiederholung.length > 0 && wiederholung !== passwort && <p className="klein fehler-text">Noch nicht gleich.</p>}
            </div>
          )}

          {modus === 'neu' && (
            <label className="test-haken">
              <input type="checkbox" checked={zugestimmt} onChange={(e) => setzeZugestimmt(e.target.checked)} />
              <span>
                Ich handle als Verantwortlicher und schließe die{' '}
                <Link to="/bedingungen" className="verweis" target="_blank">
                  Nutzungsbedingungen mit der Vereinbarung zur Auftragsverarbeitung
                </Link>{' '}
                ab.
              </span>
            </label>
          )}

          <button type="submit" className="knopf gefuellt gross" disabled={laeuft}>
            {laeuft ? 'Einen Moment …' : modus === 'neu' ? 'Zugang anlegen' : 'Anmelden'}
          </button>

          <p className="leiser klein">
            Fragen gehen an{' '}
            <a className="verweis" href="mailto:louis.honal@hks-automation.de">
              louis.honal@hks-automation.de
            </a>
            .
          </p>
        </form>
      </div>

      <footer className="test-rechtsfuss">
        {/* Neuer Tab, damit das halb ausgefuellte Formular stehen bleibt. */}
        <Link to="/impressum" className="verweis" target="_blank">
          Impressum
        </Link>
        <Link to="/datenschutz" className="verweis" target="_blank">
          Datenschutz
        </Link>
        <Link to="/bedingungen" className="verweis" target="_blank">
          Nutzungsbedingungen und AVV
        </Link>
      </footer>
    </div>
  )
}
